// src/pages/HireAgreementsPage.js
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Chip,
  Button,
  TableContainer,
  CircularProgress,
} from "@mui/material";

import useHireAgreements from "../hooks/useHireAgreements";
import useDrivers from "../hooks/useDrivers";
import useVehicles from "../hooks/useVehicles";

// ---- Helpers ----

// Same highlight style as Drivers page
function highlightMatch(text, query) {
  if (!text) return "-";
  if (!query.trim()) return text;

  const str = String(text);
  const lowerStr = str.toLowerCase();
  const lowerQuery = query.toLowerCase();

  const index = lowerStr.indexOf(lowerQuery);
  if (index === -1) return str;

  const before = str.slice(0, index);
  const match = str.slice(index, index + query.length);
  const after = str.slice(index + query.length);

  return (
    <>
      {before}
      <span
        style={{
          backgroundColor: "rgba(255, 245, 157, 0.9)",
          borderRadius: 3,
          padding: "0 2px",
        }}
      >
        {match}
      </span>
      {after}
    </>
  );
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-GB");
}

function statusColor(status) {
  switch (status) {
    case "active":
      return "success";
    case "draft":
      return "default";
    case "ended":
      return "info";
    case "cancelled":
      return "error";
    default:
      return "default";
  }
}

// Safe stringifying for id comparisons
function idToString(val) {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (val.$oid) return val.$oid;
  return String(val);
}

export default function HireAgreementsPage() {
  const navigate = useNavigate();

  const { agreements, loading, error } = useHireAgreements();
  const { drivers } = useDrivers();
  const { vehicles } = useVehicles();

  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Map drivers by _id string
  const driverById = React.useMemo(() => {
    const map = {};
    drivers.forEach((d) => {
      const key = idToString(d._id);
      if (key) map[key] = d;
    });
    return map;
  }, [drivers]);

  // Map vehicles by _id string (hire_agreements likely store vehicle_id as _id)
  const vehicleById = React.useMemo(() => {
    const map = {};
    vehicles.forEach((v) => {
      const key = idToString(v._id) || v.id; // fallback if needed
      if (key) map[key] = v;
    });
    return map;
  }, [vehicles]);

  // Filter agreements by agreement id, driver name, vehicle vrm, status
  const filteredAgreements = React.useMemo(() => {
    if (!search.trim()) return agreements;
    const q = search.trim().toLowerCase();

    return agreements.filter((a) => {
      const idStr = idToString(a._id).toLowerCase();
      const driver = driverById[idToString(a.driver_id)];
      const vehicle = vehicleById[idToString(a.vehicle_id)];

      const driverName = driver
        ? `${driver.firstname || ""} ${driver.lastname || ""}`.trim()
        : "";
      const vehicleVrm = vehicle?.vrm || "";
      const status = (a.status || "").toLowerCase();

      return (
        idStr.includes(q) ||
        (driverName && driverName.toLowerCase().includes(q)) ||
        (vehicleVrm && vehicleVrm.toLowerCase().includes(q)) ||
        status.includes(q)
      );
    });
  }, [agreements, search, driverById, vehicleById]);

  React.useEffect(() => {
    setPage(0);
  }, [search]);

  const paginatedAgreements = React.useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAgreements.slice(start, start + rowsPerPage);
  }, [filteredAgreements, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ mt: 5, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 5, textAlign: "center" }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Hire Agreements
      </Typography>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        View and manage agreements between your drivers and vehicles.
      </Typography>

      <Paper
        elevation={2}
        sx={{
          borderRadius: 2,
          p: 2,
        }}
      >
        {/* Header: search + Add button (same layout as Drivers page) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { xs: "stretch", sm: "center" },
            mb: 2,
          }}
        >
          <TextField
            label="Search hire agreements…"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ maxWidth: 320 }}
            placeholder="Search by driver, vehicle, status or ID"
          />

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="contained"
            onClick={() => navigate("/hire-agreements/new")}
            sx={{ textTransform: "none" }}
          >
            Add Hire Agreement
          </Button>
        </Box>

        {/* Table */}
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Agreement</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Driver</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Vehicle</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Start</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>End</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedAgreements.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" sx={{ py: 2 }}>
                      No hire agreements match this search.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {paginatedAgreements.map((a) => {
                const idStr = idToString(a._id);

                const driver = driverById[idToString(a.driver_id)];
                const driverName = driver
                  ? `${driver.firstname || ""} ${
                      driver.lastname || ""
                    }`.trim()
                  : "Unassigned";

                const vehicle = vehicleById[idToString(a.vehicle_id)];
                const vehicleLabel = vehicle
                  ? `${vehicle.vrm} – ${
                      vehicle.make_model || vehicle.make || ""
                    }`
                  : "Unassigned";

                return (
                  <TableRow
                    key={idStr}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "rgba(249,250,251,0.9)",
                      },
                    }}
                  >
                    {/* Agreement ID (clickable) */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          cursor: "pointer",
                          textDecoration: "underline",
                          "&:hover": { color: "primary.main" },
                        }}
                        onClick={() =>
                          navigate(`/hire-agreements/${idStr}`)
                        }
                      >
                        {highlightMatch(idStr, search)}
                      </Typography>
                    </TableCell>

                    {/* Driver (clickable when known) */}
                    <TableCell>
                      {driver ? (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            cursor: "pointer",
                            textDecoration: "underline",
                            "&:hover": { color: "primary.main" },
                          }}
                          onClick={() => navigate(`/drivers/${driver._id}`)}
                        >
                          {highlightMatch(driverName, search)}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          Unassigned
                        </Typography>
                      )}
                    </TableCell>

                    {/* Vehicle (clickable when known) */}
                    <TableCell>
                      {vehicle ? (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            cursor: "pointer",
                            textDecoration: "underline",
                            "&:hover": { color: "primary.main" },
                          }}
                          onClick={() =>
                            navigate(`/vehicles/${vehicle.id || vehicle._id}`)
                          }
                        >
                          {highlightMatch(vehicleLabel, search)}
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          Unassigned
                        </Typography>
                      )}
                    </TableCell>

                    {/* Dates */}
                    <TableCell>{formatDate(a.start_date)}</TableCell>
                    <TableCell>{formatDate(a.end_date)}</TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        size="small"
                        label={a.status || "unknown"}
                        color={statusColor(a.status)}
                        variant="outlined"
                      />
                    </TableCell>

                    {/* Created At */}
                    <TableCell>{formatDate(a.created_at)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={filteredAgreements.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Paper>
    </Box>
  );
}
